import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import RotatingElements from '@/components/rotating-elements'
import SimpleSignupForm from '@/components/simple-signup-form'
import type {HeroFragment} from '@/graphql-types'
import {getVideoSources} from '@/utils/get-video-sources'
import Image from '@helios/shared/components/image'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {FormConfig} from '@helios/shared/types/consents'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import getEnvironmentFormConfig from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

interface Props {
    slice: HeroFragment
}

const mobileBreakpoint = 'xs'

export const Hero = React.forwardRef(({slice}: Props, ref) => {
    const content = slice.primary
    const isSmallScreen = useMediaQuery.max(mobileBreakpoint)

    const rotatingSignupReasons = [
        content.signup_reason_1,
        content.signup_reason_2,
        content.signup_reason_3,
    ].filter((reason) => !!reason)

    const video = getVideoSources({
        webm: content.video_webm,
        mp4: content.video_mp4,
    })

    const trackingHeroVideoClick = () => {
        track(TrackingEvent.HomepageSectionActioned, {
            action: 'hero_video_clicked',
        })
    }

    const predefinedSignupBanner = content.predefined_signup_banner.document
    const submitText = content.submit_label ?? predefinedSignupBanner.data.submit_text

    return (
        <Section ref={ref} padding="small" data-testid="homepage-hero-container">
            <FeatureBlock
                videoId={content.wistia_video_id}
                inlineVideo={video}
                image={{...content.image, loading: 'eager'}}
                title={content.title}
                body={content.lead_text.richText}
                mirrored={!!content.inverse}
                titleVariant="heading-large"
                bodyProps={{variant: 'paragraph-large'}}
                headingLevel="h1"
                onPlayVideo={trackingHeroVideoClick}
                dataTestId="homepage-hero"
            >
                <SignupArea>
                    {predefinedSignupBanner && (
                        <SimpleSignupForm
                            {...predefinedSignupBanner.data}
                            withBorder
                            formConfig={
                                getEnvironmentFormConfig({
                                    production: predefinedSignupBanner.data.form_production,
                                    staging: predefinedSignupBanner.data.form_staging,
                                }) as FormConfig
                            }
                            placeholder={predefinedSignupBanner.data.email_input_placeholder}
                            submitText={submitText}
                            errorText={predefinedSignupBanner.data.email_input_error_text}
                            dataTestId="homepage-hero"
                        />
                    )}

                    {content.rotating_signup_reasons && rotatingSignupReasons.length > 0 && (
                        <RotatingElements>
                            {rotatingSignupReasons.map((reason, i) => (
                                <Reason key={i}>
                                    <Image image={reason.document.data.icon} />

                                    <ReasonTextWrapper>
                                        <Text variant="label-base-medium">
                                            {reason.document.data.primary}
                                        </Text>
                                        {reason.document.data.secondary && (
                                            <Text variant="label-base">
                                                {!isSmallScreen && ' ・ '}
                                                {reason.document.data.secondary}
                                            </Text>
                                        )}
                                    </ReasonTextWrapper>
                                </Reason>
                            ))}
                        </RotatingElements>
                    )}
                </SignupArea>
            </FeatureBlock>
        </Section>
    )
})

export const Reason = styled.div`
    display: grid;
    grid-template-columns: 28px auto;
    gap: ${tokens.spacing8};
    justify-content: center;
    align-items: center;

    ${mq.min(mobileBreakpoint)} {
        justify-content: start;
    }
`

export const ReasonTextWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    justify-content: start;

    ${mq.min(mobileBreakpoint)} {
        grid-template-columns: auto auto;
    }
`

const SignupArea = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
    width: 100%;
`
